import React, { useEffect, useState } from "react";
import { Download, FileText, Grid3x3, Repeat } from "lucide-react";
import { Button } from "./ui/button";
import { API, getMathDiagnosticMidpointHint } from "../lib/api";

/**
 * Two branded tracker PDFs for every Math Engine diagnostic, plus a
 * three-pass workflow (Baseline / Midpoint / End of Unit) so the
 * teacher administers the SAME 5 diagnostic questions three times
 * across the unit and tracks growth.
 *
 *   - Math Unit Tracker v5 — class roster (landscape, 1 page)
 *   - Diagnostic & Exit Ticket Tally Sheet — labelled per pass
 *     (portrait, 3 pages)
 */
const PASSES = [
  {
    key: "baseline",
    label: "Baseline",
    sub: "Before Lesson 1 — the original administration",
  },
  {
    key: "midpoint",
    label: "Midpoint",
    sub: "Halfway through the unit — same 5 questions",
  },
  {
    key: "end_of_unit",
    label: "End of unit",
    sub: "Final administration — same 5 questions",
  },
];

export default function DiagnosticTrackerDownloads({ activityId }) {
  const [hint, setHint] = useState(null);

  useEffect(() => {
    if (!activityId) return;
    let cancelled = false;
    getMathDiagnosticMidpointHint(activityId)
      .then((data) => {
        if (!cancelled) setHint(data);
      })
      .catch(() => {
        // Hint is decorative — silently ignore.
      });
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  if (!activityId) return null;

  return (
    <div
      className="ae-card p-5 md:p-6 space-y-5"
      data-testid="math-engine-tracker-downloads"
    >
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70">
          Tracker sheets · Atomic Edu brand
        </p>
        <h4 className="font-black text-xl text-[#1c2d3d] tracking-tight">
          Print these alongside the diagnostic
        </h4>
        <p className="text-sm text-[#4a6984]/80 mt-1">
          Run the same 5 questions three times so growth is visible. Both
          tracker sheets are pre-filled with the expectation codes and the
          correct-answer key from this diagnostic.
        </p>
      </div>

      {/* Class roster tracker — single landscape sheet */}
      <a
        href={`${API}/math-engine/diagnostic/${activityId}/tracker/unit-tracker`}
        target="_blank"
        rel="noopener noreferrer"
        className="group rounded-xl border border-[#4a6984]/20 bg-white px-4 py-3 hover:border-[#96ded1] hover:shadow-sm transition flex items-center gap-3"
        data-testid="math-tracker-unit-tracker"
      >
        <div className="rounded-lg bg-[#96ded1]/25 p-2.5 shrink-0">
          <Grid3x3 className="h-5 w-5 text-[#1c2d3d]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#1c2d3d] text-sm leading-tight">
            Math Unit Tracker v5
          </p>
          <p className="text-xs text-[#4a6984]/80 mt-0.5">
            Landscape · 1 page · whole-class roster · Correct Answer row
            pre-filled · Diagnostic + L1-L10 Exit Tickets
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-[#4a6984]/25 group-hover:bg-[#96ded1] group-hover:border-[#96ded1] shrink-0"
          tabIndex={-1}
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Open
        </Button>
      </a>

      {/* Three-pass tally sheets */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Repeat className="h-4 w-4 text-[#1c2d3d]" />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1c2d3d]">
            Diagnostic &amp; Exit Ticket Tally Sheet — one per pass
          </p>
        </div>
        {hint?.midpoint_message && (
          <p className="text-xs text-[#4a6984]/80 mb-3 leading-relaxed">
            <strong className="text-[#1c2d3d]">Pacing tip:</strong>{" "}
            {hint.midpoint_message}{" "}
            {hint.end_message && <span>{hint.end_message}</span>}
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PASSES.map(({ key, label, sub }) => (
            <a
              key={key}
              href={`${API}/math-engine/diagnostic/${activityId}/tracker/diagnostic-exit/${key}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-xl border border-[#4a6984]/20 bg-white px-4 py-3 hover:border-[#96ded1] hover:shadow-sm transition"
              data-testid={`math-tracker-pass-${key}`}
            >
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-[#96ded1]/25 p-2 shrink-0">
                  <FileText className="h-4 w-4 text-[#1c2d3d]" />
                </div>
                <p className="font-bold text-[#1c2d3d] text-sm">{label}</p>
              </div>
              <p className="text-[11px] text-[#4a6984]/80 mt-1.5 leading-snug">
                {sub}
              </p>
              <p className="text-[10px] text-[#4a6984] font-bold uppercase tracking-wider mt-2 group-hover:text-[#1c2d3d]">
                Open PDF →
              </p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
